/**
 * Returns true only if the URL is a safe relative path.
 * Rejects protocol-relative (`//`), backslash-relative (`/\`), and absolute URLs (`://`).
 * Prevents open redirect attacks.
 */
const isRelativePath = (url: string): boolean => {
  if (!url.startsWith('/')) return false
  if (url.startsWith('//') || url.startsWith('/\\')) return false
  if (url.includes('://')) return false
  return true
}

/**
 * Returns the url if it passes `isRelativePath`, otherwise returns the fallback.
 */
export const sanitizeRedirectUrl = (url: string, fallback: string): string => {
  return isRelativePath(url) ? url : fallback
}

/**
 * Validates that a URL string resolves to the same origin as `window.location.origin`.
 * Returns false for invalid or cross-origin URLs.
 */
export const isSameOriginUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}



const SENSITIVE_KEY_PATTERN = /token|password|secret|key|authorization/i

/**
 * Strips sensitive keys from objects for safe production logging.
 * Recursively processes nested objects, replaces arrays with '[Array]',
 * and redacts values whose keys match common secret patterns.
 */
export const stripSensitiveLogData = (data: unknown): unknown => {
  if (data === null || data === undefined) return null
  if (typeof data === 'string') return data
  if (typeof data !== 'object') return data
  if (Array.isArray(data)) return '[Array]'

  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if (SENSITIVE_KEY_PATTERN.test(k)) {
      result[k] = '[REDACTED]'
    } else if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      result[k] = stripSensitiveLogData(v)
    } else if (Array.isArray(v)) {
      result[k] = '[Array]'
    } else {
      result[k] = v
    }
  }
  return result
}
